import {
  IconChartLine,
  IconDatabase,
  IconDatabaseShare,
  IconFilter,
  IconLink,
  IconListCheck,
  IconListDetails,
} from '@tabler/icons-react';
import type { ComponentType } from 'react';

import type { MantineColor } from '#core/lib/formatters/colors.ts';

/**
 * Coordinated semantic palette for the tool-display chrome. Each name
 * pairs a color, shade, and icon so the visual language stays
 * consistent across tools that play similar roles. Tools should prefer
 * picking a {@link ToolSemantic} on `display.complete.semantic` over
 * specifying raw `color` / `icon` overrides.
 *
 * Derived from {@link SEMANTIC_TOKENS} so the keys cannot drift — adding
 * or removing a semantic token automatically updates the union.
 */
export type ToolSemantic = keyof typeof SEMANTIC_TOKENS;

export type SemanticToken = {
  color: MantineColor;
  shade: number;
  icon: ComponentType<{ size?: number | string; color?: string }>;
};

/**
 * Coordinated color+shade+icon palette for tool display chrome. Pick a
 * semantic name on the tool's `display.complete.semantic` field; the
 * chrome looks up the token here. Tools should prefer semantics over
 * raw color/icon overrides — keeps the visual language consistent.
 */
export const SEMANTIC_TOKENS = {
  // Storing literal/computed data — the canonical "side-effect on the
  // dataset store" tool. Cyan picks up RegisterDataset's existing color.
  'side-effect': { color: 'cyan', shade: 4, icon: IconDatabase },
  // Transforming an already-stored dataset (filter / derive / sort / rollup).
  'data-transform': { color: 'blue', shade: 4, icon: IconFilter },
  // Joining two stored datasets.
  'data-join': { color: 'blue', shade: 4, icon: IconLink },
  // Discovering view schema (cheap metadata pre-flight).
  'view-schema': { color: 'grape', shade: 3, icon: IconListDetails },
  // Querying a CDF view to stage a typed dataset.
  'view-query': { color: 'grape', shade: 3, icon: IconDatabaseShare },
  // Up-front planning before any tool calls.
  plan: { color: 'teal', shade: 4, icon: IconListCheck },
  // Bulk timeseries datapoint fetches for charting.
  'data-source': { color: 'cyan', shade: 4, icon: IconChartLine },
} as const satisfies Record<string, SemanticToken>;
